import { Vector3 } from 'three';

/**
 * The named viewpoints the view cube offers.
 *
 * Directions are the offset from the model centre to the camera, expressed in
 * scene space. IFC is Z-up and the scene is Y-up, so the mapping is the same
 * one `section.ts` documents: model X -> scene X, model Y (plan depth) ->
 * scene Z, model Z (height) -> scene Y. Front therefore means "camera in front
 * of the building", at model -Y.
 */
export type StandardView = 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right' | 'iso';

export const STANDARD_VIEWS: readonly StandardView[] = [
  'top',
  'bottom',
  'front',
  'back',
  'left',
  'right',
  'iso',
];

// A dead-on vertical view is parallel to the camera's up vector, which makes
// the orientation ambiguous and flips the view; a hair of tilt avoids it.
const VERTICAL_TILT = 0.001;

const DIRECTIONS: Record<StandardView, readonly [number, number, number]> = {
  top: [0, 1, VERTICAL_TILT],
  bottom: [0, -1, VERTICAL_TILT],
  front: [0, 0, -1],
  back: [0, 0, 1],
  left: [-1, 0, 0],
  right: [1, 0, 0],
  iso: [1, 0.8, 1],
};

export const VIEW_LABELS: Record<StandardView, string> = {
  top: 'Top',
  bottom: 'Bottom',
  front: 'Front',
  back: 'Back',
  left: 'Left',
  right: 'Right',
  iso: 'Iso',
};

/** Offset from the model centre to the camera for a named view. */
export function directionForView(view: StandardView): Vector3 {
  return new Vector3(...DIRECTIONS[view]);
}
